/* ============================================================================
 * MTE - Profiles (persistence localStorage, multi-profile).
 * ----------------------------------------------------------------------------
 * Profile system foundational - generic schema supporting N profiles.
 * Default seed = single Leo demo profile (backward compat Vague 2).
 *
 * Storage layout :
 *   mte:profiles:v1         - JSON array of Profile objects
 *   mte:active-profile:v1   - JSON string (active profile id, or null)
 *
 * Id generation : crypto.randomUUID() with fallback to local-<ts>-<rand>.
 * SSR / sandbox safe via try/catch window.localStorage.
 * ========================================================================== */
import type { HeroId } from '../components/mte/placeholders';

export interface Profile {
  id: string;
  firstName: string;
  schoolLevel: 'CP' | 'CE1' | 'CE2' | 'CM1' | 'CM2';
  heroId: HeroId | null;
  createdAt: string;
}

export interface ProfilesState {
  profiles: Profile[];
  activeProfileId: string | null;
}

const PROFILES_KEY = 'mte:profiles:v1';
const ACTIVE_KEY = 'mte:active-profile:v1';

function lsGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function lsUnset(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function filterById<T extends { id: string }>(items: T[], id: string): T[] {
  return items.filter((it) => it.id !== id);
}

function mapById<T extends { id: string }>(items: T[], id: string, patch: Partial<T>): T[] {
  return items.map((it) => (it.id === id ? { ...it, ...patch, id: it.id } : it));
}

function isProfile(value: unknown): value is Profile {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.firstName === 'string' &&
    (v.schoolLevel === 'CP' || v.schoolLevel === 'CE1' || v.schoolLevel === 'CE2' || v.schoolLevel === 'CM1' || v.schoolLevel === 'CM2') &&
    (v.heroId === null || typeof v.heroId === 'string') &&
    typeof v.createdAt === 'string'
  );
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      /* fall through */
    }
  }
  return 'local-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

export function defaultProfilesState(): ProfilesState {
  const leo: Profile = {
    id: 'demo-leo',
    firstName: 'Léo',
    schoolLevel: 'CE1',
    heroId: null,
    createdAt: '2026-09-13T00:00:00.000Z',
  };
  return { profiles: [leo], activeProfileId: leo.id };
}

export function loadProfiles(): Profile[] {
  const raw = lsGet(PROFILES_KEY);
  if (!raw) {
    const seed = defaultProfilesState();
    saveProfiles(seed.profiles);
    setActiveProfileId(seed.activeProfileId);
    return seed.profiles;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return defaultProfilesState().profiles;
    return parsed.filter(isProfile);
  } catch {
    return defaultProfilesState().profiles;
  }
}

export function saveProfiles(profiles: Profile[]): void {
  lsSet(PROFILES_KEY, JSON.stringify(profiles));
}

export function addProfile(p: Omit<Profile, 'id' | 'createdAt'>): Profile {
  const profile: Profile = {
    ...p,
    id: generateId(),
    createdAt: new Date().toISOString(),
  };
  const profiles = loadProfiles();
  saveProfiles([...profiles, profile]);
  if (profiles.length === 0) {
    setActiveProfileId(profile.id);
  }
  return profile;
}

export function updateProfile(id: string, patch: Partial<Profile>): void {
  const profiles = loadProfiles();
  const updated = mapById(profiles, id, patch);
  saveProfiles(updated);
}

export function removeProfile(id: string): void {
  const profiles = filterById(loadProfiles(), id);
  saveProfiles(profiles);
  if (getActiveProfileId() === id) {
    const next = profiles[0]?.id ?? null;
    setActiveProfileId(next);
  }
}

export function getActiveProfileId(): string | null {
  const raw = lsGet(ACTIVE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export function setActiveProfileId(id: string | null): void {
  if (id === null) {
    lsUnset(ACTIVE_KEY);
    return;
  }
  const profiles = loadProfiles();
  if (!profiles.some((p) => p.id === id)) {
    lsUnset(ACTIVE_KEY);
    return;
  }
  lsSet(ACTIVE_KEY, JSON.stringify(id));
}

export function getActiveProfile(): Profile | null {
  const id = getActiveProfileId();
  if (!id) {
    const profiles = loadProfiles();
    return profiles[0] ?? null;
  }
  const profiles = loadProfiles();
  return profiles.find((p) => p.id === id) ?? profiles[0] ?? null;
}

export function resetAllProfiles(): void {
  lsUnset(PROFILES_KEY);
  lsUnset(ACTIVE_KEY);
}
